<template>
  <div class="main-container" v-if="isLoaded">
    <team-popup :team="members"
      :ownerLinkedAccounts="ownerLinkedAccounts"
      :isOwnerPhoneVerified="isOwnerPhoneVerified"
      @close="isTeamPopupVisible = false"
      v-if="isTeamPopupVisible" />
    <div class="header-container">
      <div class="title-and-date-container">
        <div class="title-container">
          <div class="title">{{title}}</div>
          <div class="subtitle">{{subtitle}}</div>
          <div class="team-container" @click="isTeamPopupVisible = true"
            v-ga="'eventTeamPopup'" v-if="owner">
            <span>by {{ownerDisplayName}} (Learn More)</span>
          </div>
          <span v-else class="unknown-owner">Unknown User</span>
        </div>
        <div class="date-container">
          <span class="event-day">{{formatDate(startDate, 'D').toUpperCase()}}</span>
          <span class="event-month">{{formatDate(startDate, 'M').toUpperCase()}}</span>
          <span class="event-date">{{formatDate(startDate, 'd')}}</span>
        </div>
      </div>
      <div class="location-container" v-if="isOnline">
        <span class="online-text">Online Event</span>
      </div>
      <div class="location-container" v-else
        @click="$refs.map.scrollIntoView()" v-ga="'eventMapScroll'">
        <div class="location-title">
          {{location.title}}
        </div>
        <div class="location-vicinity">
          {{location.vicinity}}
        </div>
      </div>
      <div class="share-contact-container">
        <contact-user :receiverUserName="ownerUserName" v-if="!isMyEvent && owner"
          buttonText="Contact Organizer" v-ga="'eventContactUserPopupTop'"
          :receiverDisplayName="ownerDisplayName" class="contact-user" />
        <share-url :url="this.$route.path" buttonText="Share" v-ga="'eventSharePopupTop'"
          :title="`Check out this awesome event - ${title}`" :description="subtitle"/>
      </div>
    </div>
    <div class="mid-container">
      <div class="left-container">
        <img v-if="pic" :src="pic" :alt="title"/>
        <div class="pitch1" v-html="pitch"></div>
      </div>
      <div class="right-container">
        <div class="category-container">
          <span class="category-title">CATEGORY</span>
          <div class="category">
            <font-awesome-icon :icon="['fa', 'thumbtack']"></font-awesome-icon>
            <span class="category-name">{{category}}</span>
          </div>
          <div class="category">
            <font-awesome-icon :icon="['fa', 'tag']"></font-awesome-icon>
            <span class="tags-name">{{tags.join(', ')}}</span>
          </div>
        </div>
        <div class="media-coverage-container" v-if="mediaCoverage.length > 0">
          <span class="media-coverage-title">MEDIA COVERAGE</span>
          <div class="media-coverage" v-for="(coverage, i) in mediaCoverage" :key="i">
            <font-awesome-icon :icon="['fa', mediaCoverageIcon(coverage.key)]">
            </font-awesome-icon>
            <span>{{coverage.value}}</span>
          </div>
        </div>
        <div class="date-range-container">
          <span class="date-range-title">EVENT DATE</span>
          <!-- <font-awesome-icon :icon="['fa', 'calendar-week']"></font-awesome-icon> -->
          <span class="event-date-and-time-range-text">
            {{formatEventRange(startDate, endDate)}}
          </span>
        </div>
        <links-view-container title="EVENT LINKS" class="links-container"
          :value="links"></links-view-container>
        <div class="market-scope-container">
          <span class="event-audience-title">MARKET SCOPE</span>
          <div class="market-scope">
            <font-awesome-icon :icon="['fa', 'users']"></font-awesome-icon>
            <span class="market-data">{{audienceSize}}</span>
          </div>
          <div class="market-scope">
            <font-awesome-icon :icon="['fa', 'globe']"></font-awesome-icon>
            <span class="market-data">{{marketReach}}</span>
          </div>
        </div>
        <div class="market-container">
          <div class="market-item-container">
            <span class="market-info-title">DEMOGRAPHY</span>
            <div class="gender-distribution">
              <div class="male-data">
                <span class="market-data">{{genderDistribution}}%<p>Male</p></span>
                <font-awesome-icon :icon="['fa', 'male']"></font-awesome-icon>
              </div>
              <div class="female-data">
                <font-awesome-icon :icon="['fa', 'female']"></font-awesome-icon>
                <span class="market-data">{{100 - genderDistribution}}%<p>Female</p></span>
              </div>
            </div>
          </div>
          <div class="market-item-container">
            <span class="market-info-title">AGE GROUP</span>
            <div class="age-group-container">
              <donut-chart
                :initialValues="ageGroups.map(a => a.percentage)"
                :colors="ageGroupColors">
              </donut-chart>
              <div class="age-group-legend-container">
                <div v-for="(ageGroup, i) in ageGroups" :key="i" class="age-group-legend-item">
                  <div class="age-group-legend-box"
                    :style="{backgroundColor: ageGroupColors[i]}"></div>
                  <span class="market-data">{{ageGroup.title}}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="pitch2" v-html="pitch" :class="{'hidden-pitch': isPitchHidden}">
        </div>
        <button v-if="isPitchHidden" aria-label="Show More"
          class="pitch2 add-link-button pitch2-margin"
          @click="togglePitchVisibility()">Show More</button>
        <button v-else class="pitch2 add-link-button pitch2-margin"
          aria-label="Show Less"
          @click="togglePitchVisibility()">Show Less</button>
        <div class="pricing-packages">
          <event-pricing-package
            class="pricing-package"
            v-for="(pricingPackage, i) in pricingPackages" :key="i"
            :id="pricingPackage.id"
            :title="pricingPackage.title"
            :amount="pricingPackage.amount"
            :maxAmount="pricingPackage.maxAmount"
            :quantity="pricingPackage.quantity"
            :deadline="pricingPackage.deadline"
            :offerings="pricingPackage.offerings"
            :deliverables="pricingPackage.deliverables"
            :description="pricingPackage.description"
            :eventId="eventId">
            </event-pricing-package>
        </div>
      </div>
    </div>
    <!-- <div class="market-and-metric-container"> -->
      <!-- COMMENTED OUT THE METRICS FEATURE ON FRONT END FOR NOW -->
      <!-- <div class="metric-container" v-if="metrics.length > 0">
        <span class="market-info-title">EVENT METRICS</span>
        <div v-for="(metric, i) in metrics" :key="i"
          class="metric-item-container">
          <span>{{metric.title}}</span>
          <span>{{metric.value}}</span>
          <span>{{metric.period}}</span>
          <span>{{metric.month}} / {{metric.year}}</span>
        </div>
      </div> -->
    <!-- </div> -->

    <div class="map-container">
      <div class="map" ref="map" v-if="googleMapUrl">
        <iframe
          width="100%"
          height="100%"
          frameborder="0" style="border:0"
          :src="googleMapUrl" allowfullscreen>
        </iframe>
      </div>
      <div class="organizer-contact-share">
        <div class="share-contact-container">
          <contact-user :receiverUserName="ownerUserName" v-if="!isMyEvent && owner"
            buttonText="Contact Organizer" v-ga="'eventContactUserPopupBottom'"
            :receiverDisplayName="ownerDisplayName" class="contact-user" />
          <share-url :url="this.$route.path" buttonText="Share" v-ga="'eventSharePopupBottom'"
            :title="`Check out this awesome event - ${title}`" :description="subtitle"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import flatpickr from 'flatpickr';
import EventPricingPackage from '@/components/event/view/EventPricingPackage.vue';
import LinksViewContainer from '@/components/utils/LinksViewContainer.vue';
import DonutChart from '@/components/event/view/DonutChart.vue';
import TeamPopup from '@/components/event/view/TeamPopup.vue';

export default {
  name: 'event-view',
  components: {
    EventPricingPackage,
    LinksViewContainer,
    DonutChart,
    TeamPopup,
  },
  data() {
    return {
      isLoaded: false,
      status: '',
      eventId: '',
      title: '',
      subtitle: '',
      category: '',
      tags: [],
      location: {},
      pic: '',
      startDate: null,
      endDate: null,
      pitch: '',
      links: [],
      mediaCoverage: [],
      eventCategories: [],
      members: [],
      pricingPackages: [],
      audienceSize: '',
      ageGroups: [],
      genderDistribution: 50,
      marketReach: '',
      metrics: [],
      ageGroupColors: ['mediumvioletred', 'blueviolet', 'blue', 'green', 'yellow', 'orange', 'red'],
      isPitchHidden: true,
      isTeamPopupVisible: false,
    };
  },
  computed: {
    ...mapState('auth', ['backendAxios']),
    ...mapState('user', ['userName']),
    fullPath() {
      return `${process.env.VUE_APP_URL}/${this.$route.path}`;
    },
    owner() {
      return this.members.find(member => member.role === 'owner');
    },
    ownerUserName() {
      return this.owner ? this.owner.user.userName : '';
    },
    ownerDisplayName() {
      return this.owner ? this.owner.user.displayName : '';
    },
    isMyEvent() {
      return this.ownerUserName === this.userName;
    },
    googleMapUrl() {
      if (!this.location || !this.location.title) {
        return null;
      }
      const query = `${this.location.title} ${this.location.vicinity}`;
      return `https://www.google.com/maps/embed/v1/place?key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}&q=${query}`;
    },
  },
  async mounted() {
    this.eventId = this.$route.params.eventId;
    try {
      const eventResponse = await this.backendAxios
        .get(`/sponsorship-event/id/${this.eventId}`);
      this.pricingPackages = eventResponse.data.pricingPackages;
      this.title = eventResponse.data.title;
      this.subtitle = eventResponse.data.subtitle;
      this.category = eventResponse.data.category;
      this.tags = eventResponse.data.tags;
      this.isOnline = eventResponse.data.isOnline;
      this.location = eventResponse.data.location || {};
      this.pic = eventResponse.data.pic;
      this.pitch = decodeURI(eventResponse.data.pitch || '');
      this.mediaCoverage = eventResponse.data.mediaCoverage;
      this.startDate = eventResponse.data.startDate;
      this.endDate = eventResponse.data.endDate;
      this.status = eventResponse.data.status;
      this.links = eventResponse.data.links.map(text => ({ value: text }));
      this.members = eventResponse.data.team;
      this.audienceSize = eventResponse.data.audienceSize;
      this.ageGroups = eventResponse.data.ageGroups;
      this.genderDistribution = eventResponse.data.genderDistribution || 50;
      this.marketReach = eventResponse.data.marketReach;
      this.metrics = eventResponse.data.metrics;
      this.ownerLinkedAccounts = eventResponse.data.ownerLinkedAccounts;
      this.isOwnerPhoneVerified = eventResponse.data.isOwnerPhoneVerified;

      this.isLoaded = true;
      window.prerenderReady = true;
    } catch (err) {
      this.$toasted.show(err.response.data, {
        type: 'info',
        duration: 5000,
      });
    }
  },
  metaInfo() {
    return {
      title: this.title,
      link: [{
        rel: 'canonical', href: this.$route.path,
      }],
      meta: [{
        'http-equiv': 'Content-Type',
        content: 'text/html; charset=utf-8',
      }, {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      }, {
        name: 'description',
        content: this.subtitle,
      }, {
        property: 'og:title',
        content: this.title,
      }, {
        property: 'og:site_name',
        content: 'YouPaired',
      }, {
        property: 'og:type',
        content: 'website',
      }, {
        property: 'og:url',
        content: this.fullPath,
      }, {
        property: 'og:image:secure_url',
        content: this.pic,
      }, {
        property: 'og:description',
        content: this.subtitle,
      }, {
        name: 'twitter:card',
        content: 'summary',
      }, {
        name: 'twitter:site',
        content: this.fullPath,
      }, {
        name: 'twitter:title',
        content: this.title,
      }, {
        name: 'twitter:description',
        content: this.subtitle,
      }, {
        name: 'twitter:creator',
        content: '@youpaired',
      }, {
        name: 'twitter:image:src',
        content: this.pic,
      }, {
        itemprop: 'name',
        content: this.title,
      }, {
        itemprop: 'description',
        content: this.subtitle,
      }, {
        itemprop: 'image',
        content: this.pic,
      }, {
        name: 'fb:app_id',
        content: process.env.VUE_APP_FB_APP_ID,
      }],
    };
  },
  methods: {
    formatDate(utc, format = 'Y-m-d') {
      return flatpickr.formatDate(new Date(utc), format);
    },
    formatEventRange(startDate, endDate) {
      const formattedStartDate = this.formatDate(startDate);
      const formattedEndDate = this.formatDate(endDate);
      const isOneDayEvent = formattedStartDate === formattedEndDate;
      if (!isOneDayEvent) {
        // show only time range
        return `${this.formatDate(startDate, 'M j, Y h:i K')} - ${this.formatDate(endDate, 'h:i K')}`;
      }
      return `${this.formatDate(startDate, 'M j, Y h:i K')} - ${this.formatDate(endDate, 'D, M j')}`;
    },
    mediaCoverageIcon(mediaCoverage) {
      switch (mediaCoverage) {
        case 'press_release':
          return 'newspaper';
        case 'tv':
          return 'tv';
        case 'live_stream':
          return 'laptop-code';
        case 'podcast':
          return 'podcast';
        default:
          return 'tag';
      }
    },
    togglePitchVisibility() {
      this.isPitchHidden = !this.isPitchHidden;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/youpaired-colors.scss';

.main-container {
  margin: 0 auto;
  width: 80%;
}

.header-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: 1.5rem 0;
}

.title-and-date-container {
  width: 70%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.title-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.date-container {
  width: 3rem;
  height: 5.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f2bb70;
  color: black;
}

.location-container {
  box-sizing: border-box;
  padding-left: 1rem;
  width: 30%;
  display: flex;
  flex-direction: column;
  text-align: left;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.online-text {
  font-size: 1.25rem;
}

.left-container {
  width: 70%;
}

.left-container > img {
  width: 100%;
}

.right-container {
  box-sizing: border-box;
  width: 30%;
  padding: 0 1rem;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.event-date-and-time-range-text {
  font-size: 1rem;
  margin-left: 0.5rem;
}

.pitch1 {
  font-size: 1rem;
  width: 100%;
}

.pricing-packages {
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.pricing-package {
  width: 100%;
  margin: 0.25rem;
}

.market-container {
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
}

// .metric-container,
.market-item-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #e1e1e1;
  border-radius: 0.25rem;
  margin: 0.5rem 0;
  padding: 0.5rem;
}

.organizer-contact-share {
  justify-content: center;
  display: flex;
  width:100%;
  padding: 1rem 0;
}

.pitch2 {
  display: none;
}

.hidden-pitch {
  height: 15rem;
  overflow: hidden;
  position: relative;
}

.hidden-pitch:after {
  content: "";
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 0;
  pointer-events: none;
  background-image: linear-gradient(to bottom,
                    rgba(255,255,255, 0),
                    rgba(255,255,255, 1) 90%);
  width: 100%;
  height: 10rem;
}

.mid-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 1rem 0;
  align-items: flex-start;
}

.location-title {
  font-size: 1.25rem;
  margin-left: 0.5rem
}

.location-vicinity {
  font-size: 1rem;
  color: gray;
  margin-left: 0.5rem
}

.team-container, .unknown-owner {
  padding: 1rem 0;
  font-size: 1rem;
}

.team-container {
  color: #0065c7;
  text-decoration:none;
  cursor: pointer;
}

.title {
  font-size: 2rem;
}

.subtitle {
  font-size: 1.25rem;
}

.map-container {
  margin: 2rem 0;
  width: 100%;
}

.map {
  margin-bottom: 2rem;
  width: 100%;
  height: 20rem;
}

.event-day {
  font-size: 1.25rem;
  border-bottom: black 1px solid;
}

.event-month {
  font-size: 1.25rem;
}

.event-date {
  font-size: 1.5rem;
}

.category-container {
  text-align: left;
}

.category-name, .tags-name {
  margin-left: 0.5rem;
  font-size: 1rem;
}

.media-coverage-container, .category-container, .date-range-container,
.market-scope-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  border-bottom: 1px solid #e1e1e1;
  justify-content: flex-start;
  align-items: center;
}

.media-coverage, .category {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-size: 1rem;
  margin-bottom: 1rem;
}

.market-scope {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-size: 1rem;
  margin: 1.25rem 0;
}

.date-range-container {
  justify-content: flex-start;
  padding-bottom: 1rem;
}

.market-scope-container{
  margin-bottom: 0.5rem;
  justify-content: flex-start;
}

.media-coverage > span {
  margin: 0 0.5rem 0 0;
}

.media-coverage > svg, .category > svg, .date-range-container > svg,
.market-scope > svg {
  width: 2rem;
  font-size: 1rem;
}

.start-and-end-date-and-time-container {
  display: flex;
  flex-direction: column;
}

.start-date-and-time, .end-date-and-time {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0 0.5rem;
  font-size: 1rem;
  width: 100%;
}

.category {
  width: 100%;
}

.category-title, .media-coverage-title, .date-range-title,
.event-audience-title {
  width: 100%;
  text-align: left;
  margin: 0.25rem 0 0.5rem 0.5rem;
  color: #777777;
}

// .market-and-metric-container {
//   display: flex;
//   flex-direction: row;
// }

// .metric-container {
//   margin-left: 2rem;
// }

.market-data {
  font-size: 1rem;
  font-weight: 600;
  margin-left: 0.5rem;
}

.metric-container > span, .market-item-container > span {
  margin: 0 0 0.5rem 0;
}

.market-info-title {
  color: #777777;
}

.male-data {
  display: flex;
  flex-direction: row;
  color: $male-demography;
}

.male-data > span {
  margin: 0 0.5rem;
}

.female-data {
  display: flex;
  flex-direction: row;
  color: $female-demography;
}

.female-data > span {
  margin: 0 0.5rem;
}

.gender-distribution {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-size: 5rem;
  margin: 3rem 0 3rem 0;
}

.age-group-container {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
}

.age-group-legend-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.age-group-legend-box {
  width: 1rem;
  height: 1rem;
  margin: 0.25rem;
}

.age-group-legend-item {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.links-container {
  border-bottom: 1px solid #e1e1e1;
}

.pitch2-margin {
  margin-bottom: 2rem;
}

.share-contact-container {
  display: flex;
  flex-direction: row;
}

.contact-user {
  margin-right: 1rem;
}

// smaller laptop screens and below
@media screen and (max-width: 1000px) {
  .main-container {
    box-sizing: border-box;
    width: 100%;
    padding: 0 1rem;
  }
}

// tablets and below
@media screen and (max-width: 811px) {
  // .main-container {
  //   box-sizing: border-box;
  //   width: 100%;
  //   padding: 0 1rem;
  // }
  .pitch1 {
    display: none;
  }

  .pitch2 {
    display: block;
  }

  .right-container {
    padding: 0;
  }

  .title-container {
    width: 100%;
  }
  .title-and-date-container {
    width: 100%;
  }
  .location-container {
    padding-left: 0;
    width: 100%;
    margin-bottom: 2rem;
  }

  .left-container {
    width: 100%;
  }
  .right-container {
    width: 100%;
  }
  .market-item-container {
    width: 45%;
  }

  .pricing-package {
    width: 45%;
  }
}

// mobile only
@media screen and (max-width: 425px) {
  .market-item-container {
    width: 100%;
  }
  .pricing-package {
    width: 100%;
  }
}

</style>
